import { useNavigate, useParams } from 'react-router-dom';
import DiceGame from '../components/Dice/DiceGame';
import { useGetDiceInplayBetsQuery } from '../services/dice/diceServices';
import Loading from '../components/Loading';
import { isLotus } from '../utils/domainConfig';

const formatTime = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
};

const LudoLivePage = () => {
  const navigate = useNavigate();
  const { gameId } = useParams();
  const isDice = (gameId || 'dice').toLowerCase() === 'dice';
  const gameLabel = isDice ? 'Dice' : (gameId ? gameId.charAt(0).toUpperCase() + gameId.slice(1) : 'Game');

  const { data: betsData, isLoading } = useGetDiceInplayBetsQuery(gameId || 'dice', { pollingInterval: 3000, skipPollingIfUnfocused: true, refetchOnFocus: true });

  const bets = betsData?.data || betsData?.bets || [];
  const lotus = isLotus();

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-[#0F2327] px-5 py-3 flex items-center justify-between mb-4">
        {lotus ? (
          <div className="flex items-center gap-2.5">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
            </span>
            <span className="text-white font-semibold text-base tracking-wide">{gameLabel} — Live</span>
          </div>
        ) : (
          <span className="text-white font-semibold text-base">{gameLabel} — Live</span>
        )}
        <button
          onClick={() => navigate(-1)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 text-sm transition-colors"
        >
          Back
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 px-2 items-start">
        {/* Left — Dice Game (dice only) */}
        {isDice && (
          <div className="w-full lg:w-1/2">
            <DiceGame />
          </div>
        )}

        {/* Right — Bet Table (only content for non-dice games) */}
        <div className={`w-full ${isDice ? 'lg:w-1/2' : ''} bg-white rounded shadow border border-gray-200 overflow-hidden`}>
          <div className="bg-[#0F2327] px-4 py-2.5 flex items-center justify-between">
            <span className="text-white font-semibold text-sm">Current Round Bets</span>
            <span className="text-xs text-gray-400">{bets.length} bets</span>
          </div>

          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex justify-center py-8"><Loading /></div>
            ) : isDice ? (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">#</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Client</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Bet On</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Amount</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Status</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">P/L</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {bets.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                        No bets for this round
                      </td>
                    </tr>
                  ) : (
                    bets.map((bet, idx) => (
                      <tr key={bet._id || idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}${lotus ? ' hover:bg-blue-50 transition-colors' : ''}`}>
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-500">{idx + 1}</td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          <div className="font-semibold text-gray-800 text-xs">{bet.fullName || bet.user?.fullName || '-'}</div>
                          {bet.userId && <div className="text-[10px] text-blue-500">{bet.userId}</div>}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 font-medium text-gray-800">{bet.betOn ?? '-'}</td>
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-700">{bet.stake ?? bet.amount ?? '-'}</td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium capitalize ${
                            bet.status === 'won' ? 'bg-green-100 text-green-700'
                            : bet.status === 'lost' ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {bet.status || 'pending'}
                          </span>
                        </td>
                        <td className={`px-3 py-2 border-b border-gray-100 font-semibold text-xs ${(bet.pl || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {bet.pl != null ? (bet.pl >= 0 ? '+' : '') + Number(bet.pl).toFixed(2) : '-'}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-400 text-xs whitespace-nowrap">
                          {formatTime(bet.placedAt || bet.createdAt)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">#</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Client</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Room</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Color</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Stake</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Prize Pool</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Status</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Joined At</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bets.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-10 text-center text-gray-400">
                        No bets for this round
                      </td>
                    </tr>
                  ) : (
                    bets.map((bet, idx) => (
                      <tr key={bet.userId || idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}${lotus ? ' hover:bg-blue-50 transition-colors' : ''}`}>
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-500">{idx + 1}</td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          <div className="font-semibold text-gray-800 text-xs">{bet.fullName || bet.username || '-'}</div>
                          {bet.userId && <div className="text-[10px] text-blue-500">{bet.userId}</div>}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-700 whitespace-nowrap">{bet.roomCode || bet.roomId || '-'}</td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          <span className="inline-flex items-center gap-1.5 capitalize text-gray-700">
                            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: bet.color || '#999' }} />
                            {bet.color || '-'}
                          </span>
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-700">{bet.stake ?? '-'}</td>
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-700">{bet.prizePool ?? '-'}</td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium capitalize ${
                            bet.status === 'completed' ? 'bg-green-100 text-green-700'
                            : bet.status === 'playing' ? 'bg-blue-100 text-blue-700'
                            : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {bet.status || 'waiting'}
                          </span>
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-400 text-xs whitespace-nowrap">
                          {formatTime(bet.joinedAt)}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          {bet.roomCode && (
                            <button
                              onClick={() => navigate(`/ludo/watch/${bet.roomCode}`)}
                              className="bg-[#0F2327] hover:bg-[#1a3540] text-white text-[10px] font-semibold px-2 py-1 rounded transition-colors whitespace-nowrap"
                            >
                              View Game
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LudoLivePage;
